/**
 * ExternalTodayCalendar — shows today's iCal events in the Today view.
 *
 * Ported from v1 `ExternalTodayCalendar.tsx`.
 * Adapted to v2's Zustand store + Tailwind design system.
 *
 * Renders a subtle card above the task list with today's calendar events.
 * Loads cached data first, then fetches fresh data in background.
 */

import { useMemo } from "react"
import dayjs from "dayjs"
import { useCalendarStore } from "./calendarStore"
import { CalendarEventList } from "./CalendarEventList"
import { useCurrentSubscription } from "@/features/settings/hooks/useCurrentSubscription"
import { PaymentPlan } from "@/features/settings/data/subscriptionPlans"

interface ExternalTodayCalendarProps {
  /** Current day as a Day.js instance or date string (for day-change tracking). */
  currentDay?: dayjs.Dayjs | string
}

/**
 * Pure display component — reads from calendarStore.
 * Loading/fetching is handled by useCalendarRefresh (in AppLayout).
 *
 * Calendar events are available immediately from localStorage cache
 * (hydrated synchronously in calendarStore), so no layout jump occurs.
 */
export function ExternalTodayCalendar({
  currentDay,
}: ExternalTodayCalendarProps) {
  // Subscription gating — hide calendar events for free users
  const subscription = useCurrentSubscription()
  const isFreeUser =
    subscription?.currentSubscription?.plan === PaymentPlan.Free

  const allEvents = useCalendarStore((s) => s.allEvents)

  // Stabilize the day key as a string so it doesn't create a new object each render
  const todayKey = useMemo(
    () =>
      currentDay
        ? dayjs(currentDay).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD"),
    [currentDay],
  )
  const today = useMemo(() => dayjs(todayKey), [todayKey])

  const todayEvents = useMemo(
    () =>
      allEvents.filter((e) => e.occurrenceDate === todayKey),
    [allEvents, todayKey],
  )

  if (isFreeUser || todayEvents.length === 0) return null

  return (
    <div className="grid transition-[grid-template-rows] duration-200 ease-in-out mb-7 mx-3 desk:mx-0">
      <div className="overflow-hidden rounded-lg bg-primary/5 px-3 py-3 relative flex items-center">
        <CalendarEventList
          events={todayEvents}
          period={{
            id: "external-today",
            type: "day",
            startDate: today.startOf("day"),
            endDate: today.endOf("day"),
          }}
        />
      </div>
    </div>
  )
}
